import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  bidAnalysisServices,
  BidDetails,
  BidAnalysisTemplate as IBidAnalysisTemplate,
} from 'services/bidAnalysis/bidAnalysisServices';
import classes from '../BidAnalysisTemplate/bidAnalysisTemplate.module.scss';
import { TemplateSidebar } from '../BidAnalysisTemplate/shared/components';
import reviewClasses from './bidReviewTemplate.module.scss';
// Section components will be imported when implementing full review functionality
// import {
//   ProjectInformationSection,
//   CurrentMarketStateSection,
//   CogsBuildupSection,
//   PricingCalculatorSection,
//   FssTrackingSection,
//   PackSizeOutputSection,
//   TmopCalculatorSection,
//   CompetitiveAnalysisSection,
//   ContractPriceRankingSection,
//   CommercialPriceRankingSection,
//   GlobalOutputSection,
// } from '../BidAnalysisTemplate/components/sections';

interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  status?: 'completed' | 'warning' | 'pending';
}

const BidReviewTemplate = observer(() => {
  const navigate = useNavigate();
  const { bidId } = useParams<{ bidId: string }>();
  const { BidAnalysisStore: _BidAnalysisStore, UserStore } = useStore();

  const [_templateData, setTemplateData] = useState<IBidAnalysisTemplate | null>(null);
  const [bidDetails, setBidDetails] = useState<BidDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('project-information');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [_validationResults, setValidationResults] = useState<
    Array<{
      rule_name: string;
      status: 'warning' | 'success';
      text: string;
    }>
  >([]);
  const [_showAllListModal, _setShowAllListModal] = useState(false);

  // Mock reviewers list - replace with actual API call
  const reviewers = [
    { label: 'Select reviewer', value: null },
    { label: 'John Doe', value: 'john.doe' },
    { label: 'Jane Smith', value: 'jane.smith' },
    { label: 'Mike Johnson', value: 'mike.johnson' },
  ];

  useEffect(() => {
    if (bidId) {
      loadBidData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  const loadBidData = async () => {
    if (!bidId) return;

    setLoading(true);
    try {
      const [details, validations] = await Promise.all([
        bidAnalysisServices.getBidDetails(Number(bidId)),
        bidAnalysisServices.validateBid(Number(bidId)).catch(() => []), // Load validation results
      ]);

      setBidDetails(details);
      setValidationResults(validations);
      // TODO: Load actual template data from API
      // For now, we'll use a placeholder structure
      setTemplateData(null);
    } catch (error) {
      console.error('Error loading bid data:', error);
      toast.error('Failed to load bid details', { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const productName = bidDetails?.product_name || 'Loading...';
  const templateType = bidDetails?.template_type || 'mas';
  const productType = bidDetails?.product_type || null;

  // Navigation items for sidebar
  const navigationItems: NavigationItem[] = [
    { id: 'project-information', label: 'Project Information', icon: 'pi pi-info-circle' },
    { id: 'product-details', label: 'Product Details', icon: 'pi pi-box' },
    { id: 'current-market-state', label: 'Current Market State', icon: 'pi pi-chart-line' },
    { id: 'cogs-buildup', label: 'COGS Buildup', icon: 'pi pi-calculator' },
    { id: 'pricing-calculator', label: 'Pricing Calculator', icon: 'pi pi-dollar' },
    { id: 'fss-tracking', label: 'FSS Tracking', icon: 'pi pi-list' },
    { id: 'pack-size-output', label: 'Pack Size Output', icon: 'pi pi-table' },
    { id: 'competitive-analysis', label: 'Competitive Analysis', icon: 'pi pi-users' },
    { id: 'contract-price-ranking', label: 'Contract Price Ranking', icon: 'pi pi-sort' },
    { id: 'commercial-price-ranking', label: 'Commercial Price Ranking', icon: 'pi pi-sort-alt' },
    { id: 'tmop-calculator', label: 'TMOP Calculator', icon: 'pi pi-cog' },
    { id: 'global-output', label: 'Global Output', icon: 'pi pi-file' },
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubmitForApproval = async () => {
    if (!bidId) {
      console.error('No bid ID available');
      return;
    }

    if (!selectedReviewer) {
      toast.error('Please select a reviewer', { autoClose: 5000 });
      return;
    }

    setSubmitting(true);
    try {
      // Get reviewer email from user store using the same pattern as getCurrentUserName
      const userMetadata = UserStore?.userMetadata;
      const reviewerEmail =
        (userMetadata?.userInfo as any)?.email || (userMetadata as any)?.email || selectedReviewer;

      // Send for review (changes status to "In Review" and runs validation)
      const result = await bidAnalysisServices.sendForReview(parseInt(bidId), reviewerEmail);

      // Show success message with validation results summary
      const validationErrors = result.validation_results?.filter(r => r.status === 'error') || [];
      const validationWarnings =
        result.validation_results?.filter(r => r.status === 'warning') || [];

      if (validationErrors.length > 0) {
        toast.warning(
          `Bid submitted for review with ${validationErrors.length} error(s) and ${validationWarnings.length} warning(s). Please review validation results.`,
          { autoClose: 5000 }
        );
      } else if (validationWarnings.length > 0) {
        toast.warning(
          `Bid submitted for review with ${validationWarnings.length} warning(s). Please review validation results.`,
          { autoClose: 5000 }
        );
      } else {
        toast.success('Bid submitted for review successfully!', { autoClose: 3000 });
      }

      // Navigate back to dashboard
      navigate(`/bdportal/bid-analysis/dashboard`);
    } catch (error: any) {
      console.error('Error submitting for approval:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit for approval. Please try again.';
      toast.error(`Failed to submit for approval: ${errorMessage}`, { autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsDraft = () => {
    // Navigate back to template
    navigate(`/bdportal/bid-analysis/template/${bidId}`);
  };

  const handleRequestConsultation = () => {
    // TODO: Implement consultation request
    toast.info('Consultation request feature coming soon', { autoClose: 5000 });
  };

  if (loading) {
    return (
      <div className={classes.templateWrapper}>
        <div style={{ padding: '2rem' }}>
          <Skeleton width="100%" height="4rem" style={{ marginBottom: '1rem' }} />
          <Skeleton width="100%" height="60vh" style={{ marginBottom: '1rem' }} />
          <Skeleton width="80%" height="4rem" />
        </div>
      </div>
    );
  }

  if (!bidDetails && bidId) {
    return (
      <div className={classes.templateWrapper}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '3rem', color: '#f59e0b' }}
            ></i>
            <h3 style={{ marginTop: '1rem' }}>Bid Not Found</h3>
            <p style={{ color: '#6b7280' }}>Unable to load bid details for ID: {bidId}</p>
            <Button
              label="Go to Dashboard"
              icon="pi pi-arrow-left"
              onClick={() => navigate('/bdportal/bid-analysis/dashboard')}
              style={{ marginTop: '1rem' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.templateWrapper}>
      {/* Review Header */}
      <div className={reviewClasses.reviewHeader}>
        <div className={reviewClasses.headerLeft}>
          <span className={reviewClasses.breadcrumb}>
            <i className="pi pi-home"></i>
            <span
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate('/bdportal/bid-analysis/dashboard')}
            >
              Dashboard
            </span>
            <i className="pi pi-angle-right"></i>
            <span
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate('/bdportal/bid-analysis/manager-review')}
            >
              Reviewer Dashboard
            </span>
            <i className="pi pi-angle-right"></i>
            <span>{productName}</span>
          </span>
          <h1 className={reviewClasses.mainTitle}>{productName}</h1>
        </div>
        <div className={reviewClasses.headerRight}>
          <span className={reviewClasses.reviewBadge}>In Review</span>
        </div>
      </div>

      <div className={classes.mainLayout}>
        <TemplateSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          navigationItems={navigationItems}
          activeSection={activeSection}
          onSectionClick={scrollToSection}
          templateType={templateType}
          productType={productType}
        />

        {/* Main Content */}
        <div className={classes.mainContent}>
          {/* Analysis Validation Section - Commented out per user request */}
          {/* <div className={reviewClasses.validationSection}>
            <div className={reviewClasses.sectionHeader}>
              <div>
                <h2>Analysis Validation</h2>
                <span className={reviewClasses.subtitle}>Pre-submission checklist</span>
              </div>
              <Button
                label="Show All List"
                icon="pi pi-list"
                outlined
                size="small"
                onClick={() => setShowAllListModal(true)}
                className={reviewClasses.showAllButton}
              />
            </div>
            <div className={reviewClasses.validationList}>
              {(() => {
                // Group validation results by rule_name
                const grouped: Record<string, Array<{ rule_name: string; status: 'warning' | 'success'; text: string }>> = {};
                validationResults.forEach((result) => {
                  if (!grouped[result.rule_name]) {
                    grouped[result.rule_name] = [];
                  }
                  grouped[result.rule_name].push(result);
                });

                // Show summary of each rule group
                return Object.entries(grouped).map(([ruleName, items]) => {
                  const hasWarnings = items.some(item => item.status === 'warning');
                  const hasSuccess = items.some(item => item.status === 'success');
                  const warningCount = items.filter(item => item.status === 'warning').length;
                  const successCount = items.filter(item => item.status === 'success').length;

                  return (
                    <div key={ruleName} className={reviewClasses.validationItem}>
                      {hasWarnings ? (
                        <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                      ) : (
                        <i className="pi pi-check-circle" style={{ color: '#10b981' }}></i>
                      )}
                      <div className={reviewClasses.validationContent}>
                        <span>{ruleName}</span>
                        <p>
                          {hasSuccess && successCount > 0 && `${successCount} success${successCount !== 1 ? 'es' : ''}`}
                          {hasSuccess && hasWarnings && ' • '}
                          {hasWarnings && warningCount > 0 && `${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
                          {items.length > 0 && items[0].text && ` - ${items[0].text}`}
                        </p>
                        {hasWarnings && (
                          <a 
                            href="#" 
                            className={reviewClasses.reviewLink}
                            onClick={(e) => {
                              e.preventDefault();
                              setShowAllListModal(true);
                            }}
                          >
                            Review Details
                          </a>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
              {validationResults.length === 0 && (
                <div className={reviewClasses.validationItem}>
                  <i className="pi pi-info-circle" style={{ color: '#6b7280' }}></i>
                  <div className={reviewClasses.validationContent}>
                    <span>No validation results available</span>
                    <p>Validation results will appear here once the bid is validated</p>
                  </div>
                </div>
              )}
            </div>
            <div className={reviewClasses.readyForReview}>
              <i className="pi pi-clock" style={{ fontSize: '2rem', color: '#f59e0b' }}></i>
              <div>
                <h3>Ready for Review</h3>
                <p>
                  {validationResults.filter(r => r.status === 'warning').length} warning
                  {validationResults.filter(r => r.status === 'warning').length !== 1 ? 's' : ''} require
                  attention before final approval
                </p>
              </div>
            </div>
          </div> */}

          {/* Show All List Modal - Commented out per user request (only used by Analysis Validation section) */}
          {/* <Dialog
            header="All Validation Results"
            visible={showAllListModal}
            onHide={() => setShowAllListModal(false)}
            className={reviewClasses.modal}
            style={{ width: '90vw', maxWidth: '800px' }}
            modal
          >
            <div className={reviewClasses.modalContent}>
              <div className={reviewClasses.modalList}>
                {validationResults.length === 0 ? (
                  <div className={reviewClasses.emptyModalState}>
                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', color: '#9ca3af' }}></i>
                    <p>No validation results found</p>
                  </div>
                ) : (
                  validationResults.map((result, index) => (
                    <div
                      key={index}
                      className={`${reviewClasses.modalItem} ${
                        result.status === 'warning' ? reviewClasses.modalWarningItem : reviewClasses.modalSuccessItem
                      }`}
                    >
                      <div className={reviewClasses.modalItemIcon}>
                        {result.status === 'warning' ? (
                          <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                        ) : (
                          <i className="pi pi-check-circle" style={{ color: '#10b981' }}></i>
                        )}
                      </div>
                      <div className={reviewClasses.modalItemContent}>
                        <div className={reviewClasses.modalItemHeader}>
                          <span className={reviewClasses.modalItemRule}>{result.rule_name}</span>
                          <span className={`${reviewClasses.modalItemStatus} ${
                            result.status === 'warning' ? reviewClasses.modalWarningStatus : reviewClasses.modalSuccessStatus
                          }`}>
                            {result.status.toUpperCase()}
                          </span>
                        </div>
                        <p className={reviewClasses.modalItemText}>{result.text}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Dialog> */}

          {/* Approval Workflow Section */}
          <div className={reviewClasses.approvalSection}>
            <div className={reviewClasses.sectionHeader}>
              <h2>Approval Workflow</h2>
              <span className={reviewClasses.subtitle}>Pre-submission checklist</span>
            </div>
            <div className={reviewClasses.approvalContent}>
              <div className={reviewClasses.submitSection}>
                <h3>Submit for Review</h3>
                <div className={reviewClasses.formGroup}>
                  <label>Account Manager *</label>
                  <Dropdown
                    value={selectedReviewer}
                    options={reviewers}
                    onChange={e => setSelectedReviewer(e.value)}
                    placeholder="Select reviewer"
                    className={reviewClasses.dropdown}
                    optionLabel="label"
                    optionValue="value"
                  />
                </div>
                <div className={reviewClasses.formGroup}>
                  <label>Notes</label>
                  <InputTextarea
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    placeholder="Add any specific notes or areas of focus for the reviewer..."
                    rows={5}
                    className={reviewClasses.textarea}
                  />
                </div>
              </div>
              <div className={reviewClasses.pcValidation}>
                <h3>P&C Validation</h3>
                <div className={reviewClasses.autoAssigned}>
                  <i className="pi pi-info-circle"></i>
                  <span>Auto-assigned</span>
                </div>
                <p>
                  P&C team review required for pricing &gt;$5,000. Sarah Mitchell will be
                  automatically assigned.
                </p>
              </div>
            </div>
            <div className={reviewClasses.actionButtons}>
              <Button
                label="Back To Workspace"
                icon="pi pi-arrow-left"
                outlined
                onClick={handleSaveAsDraft}
                className={reviewClasses.actionBtn}
              />
              <Button
                label="Submit For Approval"
                icon="pi pi-send"
                onClick={handleSubmitForApproval}
                loading={submitting}
                className={reviewClasses.actionBtnPrimary}
              />
              <Button
                label="Save As Draft"
                icon="pi pi-save"
                outlined
                onClick={handleSaveAsDraft}
                className={reviewClasses.actionBtn}
              />
              <Button
                label="Request Consultation"
                icon="pi pi-comments"
                severity="warning"
                outlined
                onClick={handleRequestConsultation}
                className={reviewClasses.actionBtn}
              />
            </div>
          </div>

          {/* Review Sections - TODO: Load and display actual bid data sections */}
          <div className={reviewClasses.sectionsNote}>
            <i className="pi pi-info-circle"></i>
            <p>
              Bid analysis sections will be displayed here for review. Click on sidebar items to
              navigate to specific sections.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BidReviewTemplate;
